import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { NavLink, Link, useSearchParams } from 'react-router-dom';
import ReactMapGL, { NavigationControl, Marker, Popup, Source, Layer } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import { debounce } from "lodash";
import '../styles/tree.css'
import { Navbar, Nav, NavDropdown } from 'react-bootstrap';
import TreeList from './TreeList';
import MapFilterControls from './MapFilterConrtrols';
import API_URL from "../config/api";

// The following is required to stop "npm build" from transpiling mapbox code.
// notice the exclamation point in the import.
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const mapStyles = {
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
    street: 'mapbox://styles/mapbox/streets-v12',
    dark: 'mapbox://styles/mapbox/dark-v11'
};

const Map = ({ userRole }) => {
    const [filterSpecies, setFilterSpecies] = useState([]);
    const [filterName, setFilterName] = useState([]);
    const [filterStatus, setFilterStatus] = useState([]);
    const [filterCycle, setFilterCycle] = useState([]);
    const [viewport, setViewport] = useState({
        latitude: 14.4134583,
        longitude: 121.4459429,
        zoom: 16.5,
    });
    const [selectedTree, setSelectedTree] = useState(null);
    const [treePins, setTreePins] = useState([]);
    const [treeDetails, setTreeDetails] = useState({});
    const [mapStyle, setMapStyle] = useState(mapStyles.satellite);
    const [showHeatmap, setShowHeatmap] = useState(false);
    const [searchParams] = useSearchParams();
    const treeId = searchParams.get("treeId");
    const treeInstanceId = searchParams.get("treeinstanceid");
    const [editedTree, setEditedTree] = useState(null);
    const [speciesColorMap, setSpeciesColorMap] = useState({});
    const mapRef = useRef(null);

    const user = JSON.parse(localStorage.getItem("user"));
    const isAdmin = user?.role === "admin";

    const handleViewportChange = useCallback(debounce((newViewport) => {
        setViewport(newViewport);
    }, 1), []);

    const fetchTreeData = async () => {
        try{
            const response = await fetch(`${API_URL}/map/get_tree_pins.php`
            );
            const data = await response.json();
            const detailsMap = {};
            const colors = {};
            data.forEach(item=>{
                detailsMap[item.speciesid]={
                    speciesid:item.speciesid,
                    scientificname:item.scientificname,
                    description:item.description,
                    name:item.name,
                    treecycle:item.treecycle,
                    treespecies:item.treespecies,
                    treestatus:item.treestatus
                };
                colors[item.speciesid]=item.color;
            });
            setTreeDetails(detailsMap);
            setSpeciesColorMap(colors);
            setTreePins(data);
        }catch(err){
            console.error(err);
        }
    };

    const handleMarkerClick = (event, treePin) => {
        setEditedTree({ ...selectedTree });
        event.preventDefault();
        const treeDetail = treeDetails[treePin.speciesid];

        if (treeDetail) {
            setSelectedTree({
                ...treePin,
                details: treeDetail,
            });
        }
    };

    useEffect(() => {
        fetchTreeData();
    }, []);

    useEffect(() => {

        if (!treeInstanceId) return;

        if (treePins.length === 0) return;

        const pin = treePins.find(
            p => p.treeinstanceid === treeInstanceId
        );

        if (!pin) return;

        const detail = treeDetails[pin.speciesid];

        setSelectedTree({
            ...pin,
            details: detail
        });

        if (mapRef.current) {

            const map = mapRef.current.getMap();

            map.flyTo({

                center: [
                    Number(pin.longitude),
                    Number(pin.latitude)
                ],

                zoom: 19,

                essential: true

            });

        }

    }, [treePins, treeDetails, treeInstanceId]);

    useEffect(() => {
        if (selectedTree) {
            console.log(selectedTree);
        }
    }, [selectedTree]);

    const queryTreePins = useMemo(() => {
        return treeId ? treePins.filter(pin => pin.speciesid === treeId) : treePins;
    }, [treePins, treeId]);

    // useMemo hook to filter tree pins based on selected filter arrays
    const filteredPins = useMemo(() => {
        return queryTreePins.filter(pin => {
            const speciesMatch = !filterSpecies.length || filterSpecies.includes(pin.treedata.treespecies);
            const nameMatch = !filterName.length || filterName.includes(pin.treedata.name);
            const statusMatch = !filterStatus.length || filterStatus.includes(pin.treedata.treestatus);
            const cycleMatch = !filterCycle.length || filterCycle.includes(pin.treedata.treecycle);
            return speciesMatch && nameMatch && statusMatch && cycleMatch;
        });
    }, [queryTreePins, filterSpecies, filterName, filterStatus, filterCycle]);

    const deleteTag = async () => {
        if (!selectedTree) return;
        try{
            const response = await fetch(`${API_URL}/map/delete_tree_pin.php`,
                {
                    method:"POST",
                    headers:{
                        "Content-Type":"application/json"
                    },
                    body:JSON.stringify({
                        treeinstanceid:selectedTree.treeinstanceid
                    })
                }
            );
            const result = await response.json();
            if(result.success){
                setSelectedTree(null);
                fetchTreeData();
            }
        }catch(err){
            console.error(err);
        }
    };

    const saveEditedTag = async () => {
        try{
            const response = await fetch(`${API_URL}/map/update_tree_pin.php`,
                {
                    method:"POST",
                    headers:{
                        "Content-Type":"application/json"
                    },
                    body:JSON.stringify(editedTree)
                }
            );
            const result = await response.json();
            if(result.success){
                setEditedTree(null);
                fetchTreeData();
            }
        }catch(err){
            console.error(err);
        }
    };

    const filteredGeoJSONData = (speciesId) => ({
        type: 'FeatureCollection',
        features: filteredPins.filter(pin => pin.speciesid === speciesId).map(pin => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [pin.longitude, pin.latitude]
            }
        }))
    });

    return (
    <>
        <div className="map-card">

            {/* ================= Toolbar ================= */}
            <div className="map-toolbar">

                <div className="toolbar-left">
                    <MapFilterControls
                        setFilterSpecies={setFilterSpecies}
                        setFilterName={setFilterName}
                        setFilterStatus={setFilterStatus}
                        setFilterCycle={setFilterCycle}
                    />
                </div>

                <div className="toolbar-right">

                

                </div>

            </div>

            {/* ================= Map Frame ================= */}

            <div className="map-frame">

                <ReactMapGL
                    ref={mapRef}
                    {...viewport}
                    mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                    mapStyle={mapStyle}
                    attributionControl={false}
                    onMove={handleViewportChange}
                    style={{
                        width: "100%",
                        height: "100%"
                    }}
                >

                    <div
                        style={{
                            position: "absolute",
                            top: 15,
                            right: 15,
                            zIndex: 10
                        }}
                    >
                        <NavigationControl />
                    </div>
                    {/* ================= Tree Markers ================= */}

                    {!showHeatmap &&
                        filteredPins.map((pin, index) => (
                            <Marker
                                key={index}
                                latitude={pin.latitude}
                                longitude={pin.longitude}
                            >
                                <div
                                    onClick={(event) =>
                                        handleMarkerClick(event, pin)
                                    }
                                    style={{
                                        cursor: "pointer",
                                        opacity: 0.85,
                                        transition: "0.2s"
                                    }}
                                >
                                    <i
                                        className="fa-solid fa-location-pin fa-2x"
                                        style={{
                                            color:
                                                speciesColorMap[pin.speciesid] ||
                                                "#198754",
                                            filter:
                                                "drop-shadow(0 2px 4px rgba(0,0,0,.35))"
                                        }}
                                    ></i>
                                </div>
                            </Marker>
                        ))}

                    {/* ================= Heatmap ================= */}

                    {showHeatmap &&
                        Object.entries(speciesColorMap).map(
                            ([speciesId, color]) => (
                                <Source
                                    key={speciesId}
                                    type="geojson"
                                    data={filteredGeoJSONData(speciesId)}
                                >
                                    <Layer
                                        type="heatmap"
                                        paint={{
                                            "heatmap-weight": 1,
                                            "heatmap-intensity": [
                                                "interpolate",
                                                ["linear"],
                                                ["zoom"],
                                                0,
                                                0.5,
                                                22,
                                                1
                                            ],
                                            "heatmap-color": [
                                                "interpolate",
                                                ["linear"],
                                                ["heatmap-density"],
                                                0,
                                                "rgba(255,255,255,0)",
                                                0.1,
                                                `${color.replace(
                                                    "rgb",
                                                    "rgba"
                                                ).slice(0, -1)},0.3)`,
                                                0.3,
                                                `${color.replace(
                                                    "rgb",
                                                    "rgba"
                                                ).slice(0, -1)},0.45)`,
                                                0.5,
                                                `${color.replace(
                                                    "rgb",
                                                    "rgba"
                                                ).slice(0, -1)},0.6)`,
                                                0.7,
                                                `${color.replace(
                                                    "rgb",
                                                    "rgba"
                                                ).slice(0, -1)},0.8)`,
                                                1,
                                                `${color.replace(
                                                    "rgb",
                                                    "rgba"
                                                ).slice(0, -1)},1)`
                                            ],
                                            "heatmap-radius": [
                                                "interpolate",
                                                ["linear"],
                                                ["zoom"],
                                                0,
                                                10,
                                                15,
                                                50
                                            ],
                                            "heatmap-opacity": 0.9
                                        }}
                                    />
                                </Source>
                            )
                        )}

                    {/* ================= Popup ================= */}

                    {selectedTree && (
                        <Popup
                            latitude={selectedTree.latitude}
                            longitude={selectedTree.longitude}
                            anchor="top"
                            closeOnClick={false}
                            onClose={() => setSelectedTree(null)}
                            maxWidth="340px"
                        >
                            <div className="popup-card">

                                <h4 className="mb-2">
                                    {selectedTree.details.name}
                                </h4>

                                <hr />

                                <p>
                                    <strong>Scientific Name</strong><br />
                                    {selectedTree.details.scientificname}
                                </p>

                                <p>
                                    <strong>Description</strong><br />
                                    {selectedTree.details.description}
                                </p>

                                <p>
                                    <strong>Planter</strong><br />
                                    {selectedTree.plantername}
                                </p>

                                <p>
                                    <strong>Date Pinned</strong><br />
                                    {selectedTree.dateadded}
                                </p>

                                <p>
                                    <strong>Status</strong><br />
                                    {selectedTree.status}
                                </p>
                                                                {isAdmin && user && (
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            marginTop: "15px",
                                            marginBottom: "15px"
                                        }}
                                    >
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={deleteTag}
                                        >
                                            Delete Tag
                                        </button>

                                        <button
                                            className="btn btn-warning btn-sm"
                                            onClick={() =>
                                                setEditedTree(selectedTree)
                                            }
                                        >
                                            Edit Tag
                                        </button>
                                    </div>
                                )}

                                <Link
                                    to={`/TreeInfo?treeId=${selectedTree.speciesid}`}
                                    className="btn btn-success w-100"
                                >
                                    Read More
                                </Link>

                                {editedTree &&
                                    selectedTree.treeinstanceid ===
                                        editedTree.treeinstanceid && (
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                saveEditedTag();
                                            }}
                                            className="mt-3"
                                        >
                                            <div className="mb-2">
                                                <label>Latitude</label>

                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    value={
                                                        editedTree.latitude || ""
                                                    }
                                                    onChange={(e) =>
                                                        setEditedTree({
                                                            ...editedTree,
                                                            latitude:
                                                                e.target.value
                                                        })
                                                    }
                                                />
                                            </div>

                                            <div className="mb-2">
                                                <label>Longitude</label>

                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    value={
                                                        editedTree.longitude ||
                                                        ""
                                                    }
                                                    onChange={(e) =>
                                                        setEditedTree({
                                                            ...editedTree,
                                                            longitude:
                                                                e.target.value
                                                        })
                                                    }
                                                />
                                            </div>

                                            <div className="mb-2">
                                                <label>Planter Name</label>

                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    value={
                                                        editedTree.plantername ||
                                                        ""
                                                    }
                                                    onChange={(e) =>
                                                        setEditedTree({
                                                            ...editedTree,
                                                            plantername:
                                                                e.target.value
                                                        })
                                                    }
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label>Status</label>

                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    value={
                                                        editedTree.status || ""
                                                    }
                                                    onChange={(e) =>
                                                        setEditedTree({
                                                            ...editedTree,
                                                            status:
                                                                e.target.value
                                                        })
                                                    }
                                                />
                                            </div>

                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "10px"
                                                }}
                                            >
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-sm"
                                                >
                                                    Save
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() =>
                                                        setEditedTree(null)
                                                    }
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    )}
                            </div>
                        </Popup>
                    )}
                </ReactMapGL>
            </div>

            <label className="me-3"
                    style={{
                        color: "#fffefe",
                        fontWeight: "bold"
                    }}>
                        <input
                            type="checkbox"
                            checked={showHeatmap}
                            onChange={(e) => setShowHeatmap(e.target.checked)}
                        />
                        {" "}Heatmap
                    </label>

                    <label className="me-3"
                    style={{
                        color: "#ffffff",
                        fontWeight: "bold"
                    }}>
                        <input
                            type="radio"
                            name="mapStyle"
                            checked={mapStyle === mapStyles.satellite}
                            onChange={() => setMapStyle(mapStyles.satellite)}
                        />
                        {" "}Satellite
                    </label>

                    <label className="me-3" style={{
                        color: "#ffffff",
                        fontWeight: "bold"
                    }}>
                        <input
                            type="radio"
                            name="mapStyle"
                            checked={mapStyle === mapStyles.street}
                            onChange={() => setMapStyle(mapStyles.street)}
                        />
                        {" "}Street
                    </label>

                    <label className="me-3" style={{
                        color: "#fcfcfc",
                        fontWeight: "bold"
                    }}>
                        <input
                            type="radio"
                            name="mapStyle"
                            checked={mapStyle === mapStyles.dark}
                            onChange={() => setMapStyle(mapStyles.dark)}
                        />
                        {" "}Dark
                    </label>
        </div>
    </>
);
};

export default Map;